'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FacetOption } from '../config';

interface FilterMorePopupProps {
  /** The filter category's name — the popup's header (BCP-2062 §6). */
  title: string;
  /** ALL values for the category, including the five already shown inline. */
  options: FacetOption[];
  /** The COMMITTED selection, read on open. */
  selected: string[];
  /** Commits the full new selection. Called by Apply only. */
  onApply: (selected: string[]) => void;
  onClose: () => void;
}

/**
 * The "More…" overflow popup (BCP-2062 §6, BCP-2063 §8).
 *
 * Ported from the hotels vertical's `FilterMoreModal`, which the ticket
 * explicitly points at ("The More popup shall display (same like hotel
 * vertical)"), and restyled onto the `atr-*` CSS the rest of this panel uses.
 * It is a COPY rather than a shared import on purpose: route isolation is a
 * hard requirement in this repo — an Attractions visitor must not download
 * hotels JS/CSS — and the hotels original is written in that domain's Tailwind
 * brand classes.
 *
 * STAGED, unlike the inline panel. Selections here are held locally and
 * committed by Apply; the inline checkboxes apply immediately. The two surfaces
 * differ by design: this one exists only for categories with a long value list,
 * where batch selection behind Apply is the whole point.
 *
 *   Apply  → commit, results refresh, chips and inline checkboxes re-sync
 *   ✕      → uncommitted changes discarded, committed state untouched
 *   Clear  → clears THIS category only — distinct from the global Reset, which
 *            clears every category and restores Our Recommendations
 */
export function FilterMorePopup({
  title,
  options,
  selected,
  onApply,
  onClose,
}: FilterMorePopupProps) {
  const [draft, setDraft] = useState<string[]>(selected);
  const [mounted, setMounted] = useState(false);

  // The portal target only exists in the browser. Reading `document` at render
  // time would abort hydration for the whole page.
  useEffect(() => {
    setMounted(true);
  }, []);

  // Re-seed from the committed selection whenever the popup is reopened —
  // "previously selected filter values shall remain selected when the popup is
  // reopened" (BCP-2062 §6).
  useEffect(() => {
    setDraft(selected);
  }, [selected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (!mounted) return null;

  const toggle = (value: string) =>
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  return createPortal(
    <div className="atr-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="atr-modal__scrim" onClick={onClose} />
      <div className="atr-modal__card">
        <div className="atr-modal__head">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="atr-modal__body">
          {options.map((option) => (
            <label key={option.value} className="atr-check atr-check--counted">
              <span>
                <input
                  type="checkbox"
                  checked={draft.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
                {option.label}
              </span>
              <span className="atr-check__count">({option.count})</span>
            </label>
          ))}
        </div>

        <div className="atr-modal__foot">
          <button type="button" className="atr-btn atr-btn-ghost" onClick={() => setDraft([])}>
            Clear
          </button>
          <button
            type="button"
            className="atr-btn atr-btn-primary"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
